import { useCallback, useRef, useState } from 'react';

import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';

import Navigation from '../navigation/Navigation';

import { getLocal, resetProfile, sendAction, updateProfile } from '../../utils/api';

import './Profile.css';

function Profile({ handleError, handleFeedback }) {
  const { logout } = useAuth0();
  const inputRef = useRef(null);

  const [filterActive, setFilterActive] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [page, setPage] = useState(0);
  const [profile, setProfile] = useState(getLocal('profile'));

  const handlePage = useCallback(page => {
    setPage(page);

    if (page !== null) {
      setIsEditing(!profile.meta_data[page].value);
    }
  }, [profile]);

  const handleFilter = useCallback(filter => {
    const paramIndex = profile?.meta_data?.findIndex(param => param.category === filter);
    
    if (paramIndex >= 0) {
      handlePage(paramIndex);
    } else {
      handlePage(null);
    }

    setFilterActive(filter);
  }, [handlePage, profile]);

  const submitProfile = useCallback(async (param, value) => {
    setIsSending(true);

    try {
      const response = await updateProfile(param, value);

      if (response !== null) {
        await sendAction('profileupdate');
        
        setProfile(response);
        setIsEditing(false);
      }
    } catch (e) {
      handleError(e);
    }

    setIsSending(false);
  }, [handleError]);

  const handleProfile = useCallback(async (param, e) => {
    let value;

    if (typeof e !== 'string') {
      if (e.keyCode === 13) {
        value = e.target.value;
      }
    } else {
      value = e;
    }

    if (value !== undefined) {
      await submitProfile(param, value ? value : null);
    }
  }, [submitProfile]);

  const handleReset = useCallback(async () => {
    if (window.confirm('Reset and logout?')) {
      logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });

      await resetProfile();
    }
  }, [logout]);
  
  const handleSubmit = useCallback(async (param) => {
    const value = inputRef.current.value;

    await submitProfile(param, value.length ? value : null);
  }, [submitProfile]);

  return (
    <div id="content">
      <div id="profile">
        <div id="filters">
          <div className={'filter' + (filterActive === 'profile' ? ' active' : '')}><img src={process.env.PUBLIC_URL + '/assets/icons/user.svg'} alt="Profile" onClick={() => handleFilter('profile')} /></div>
          <div className={'filter' + (filterActive === 'academic' ? ' active' : '')}><img src={process.env.PUBLIC_URL + '/assets/icons/academic.svg'} alt="Academic" onClick={() => handleFilter('academic')} /></div>
          <div className={'filter' + (filterActive === 'financial' ? ' active' : '')}><img src={process.env.PUBLIC_URL + '/assets/icons/financial.svg'} alt="Financial" onClick={() => handleFilter('financial')} /></div>
          <div className={'filter' + (filterActive === 'wellbeing' ? ' active' : '')}><img src={process.env.PUBLIC_URL + '/assets/icons/wellbeing.svg'} alt="Wellbeing" onClick={() => handleFilter('wellbeing')} /></div>
          <div className={'filter' + (filterActive === 'career' ? ' active' : '')}><img src={process.env.PUBLIC_URL + '/assets/icons/career.svg'} alt="Career" onClick={() => handleFilter('career')} /></div>
        </div>
        <div className="instructions">Your user profile data is based on information that you have provided. You can view and edit it here at any time. If you would like to delete your profile data please email <a href="mailto:support@campusevolve.ai">support@campusevolve.ai</a> for assistance.</div>
        {isEditing ? (
          <div id="card" className={profile.meta_data[page].type}>
            <div className="header">
              <img id="edit" src={process.env.PUBLIC_URL + '/assets/icons/close.svg'} alt="Close" onClick={() => setIsEditing(false)} />
            </div>
            <div className="body">
              <div className="prompt">{profile.meta_data[page].prompt}</div>
              <div className="values">
                {profile.meta_data[page].values?.map((v, i) => (
                  <div key={i} className="box" onClick={() => handleProfile(profile.meta_data[page].param, v)}>{v}</div>
                ))}
              </div>
            </div>
            <div className="footer">
              <div className="text-input">
                {/* <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" /> */}
                <input ref={inputRef} type="input" name="profile" placeholder={isSending ? 'Give me a sec...' : 'Tap to respond'} autoComplete="off" disabled={isSending} onKeyDown={(e) => handleProfile(profile.meta_data[page].param, e)} />
                <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => handleSubmit(profile.meta_data[page].param)} />
              </div>
            </div>
          </div>
        ) : (
          <div id="card">
            <div className="header"></div>
            <div className="body">
              {profile?.meta_data?.length && page !== null ? (
                <>
                  <div className="label">{profile.meta_data[page].label}:</div>
                  <div className="value">{profile.meta_data[page].value ? profile.meta_data[page].value : '-'}</div>
                </>
              ) : null}
            </div>
            <div className="footer">
              {page !== null ? (
                <img id="edit" src={process.env.PUBLIC_URL + '/assets/icons/edit.svg'} alt="Edit" onClick={() => setIsEditing(true)} />
              ) : null}
            </div>
          </div>
        )}
        <div id="pager">
          {profile?.meta_data?.length && profile.meta_data.map((param, i) => {
            if (param.category === filterActive) {
              return <div key={i} className={'page' + (page === i ? ' active' : '')} onClick={() => { setIsEditing(false); handlePage(i); }}></div>
            }
            
            return null;
          })}
        </div>
        <div id="reset" onClick={handleReset}></div>
      </div>
      <Navigation handleFeedback={handleFeedback} />
    </div>
  );
}

export default withAuthenticationRequired(Profile);